import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Sparkle, MagnifyingGlass, Eye, Database, Crown,
  ArrowRight, X, Confetti, Rocket, BookOpen,
} from '@phosphor-icons/react';

const TOUR_KEY = 'fh_tour_seen_v1';

const DashboardTour = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const steps = useMemo(() => ([
    {
      icon: Confetti, color: '#F59E0B',
      title: t('tour.welcome_title'),
      body: t('tour.welcome_body'),
      cta: t('tour.lets_go'),
    },
    {
      icon: MagnifyingGlass, color: '#3B82F6',
      title: t('tour.step2_title'),
      body: t('tour.step2_body'),
      cta: t('tour.next'),
      link: '/funnel-spy',
      linkLabel: t('nav.funnel_spy'),
    },
    {
      icon: Sparkle, color: '#A855F7',
      title: t('tour.step3_title'),
      body: t('tour.step3_body'),
      cta: t('tour.next'),
      link: '/auto-build',
      linkLabel: t('nav.auto_build'),
    },
    {
      icon: Eye, color: '#10B981',
      title: t('tour.step4_title'),
      body: t('tour.step4_body'),
      cta: t('tour.next'),
      link: '/exports',
      linkLabel: t('nav.export_hub'),
    },
    {
      icon: Rocket, color: '#F59E0B',
      title: t('tour.step5_title'),
      body: t('tour.step5_body'),
      cta: t('tour.last'),
      secondary: t('common.more'),
      secondaryLink: '/guia',
    },
  ]), [t]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const seen = window.localStorage.getItem(TOUR_KEY);
    if (!seen) {
      // small delay so the dashboard renders first
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const close = () => {
    try {
      window.localStorage.setItem(TOUR_KEY, '1');
    } catch (e) { /* localStorage unavailable */ }
    setOpen(false);
  };

  const next = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else close();
  };

  if (!open) return null;
  const current = steps[step];
  const Icon = current.icon;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm"
      data-testid="dashboard-tour-overlay"
    >
      <div className="relative w-full max-w-md bg-gradient-to-b from-[#0A0A0A] to-[#050505] border border-white/10 rounded-3xl shadow-2xl shadow-blue-500/10 p-7">
        <button
          onClick={close}
          data-testid="tour-close-btn"
          aria-label={t('common.close')}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
        >
          <X size={16} weight="bold" />
        </button>

        {/* progress */}
        <div className="flex items-center gap-1.5 mb-6">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-[#3B82F6]' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
          style={{ backgroundColor: `${current.color}22` }}
        >
          <Icon size={26} weight="bold" style={{ color: current.color }} />
        </div>

        <h3 className="text-2xl font-[Outfit] font-bold mb-3 leading-tight">{current.title}</h3>
        <p className="text-white/70 leading-relaxed mb-6">{current.body}</p>

        <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2">
          {current.link && (
            <Link
              to={current.link}
              onClick={close}
              data-testid="tour-link-btn"
              className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-center transition"
            >
              {current.linkLabel}
            </Link>
          )}
          {current.secondary && (
            <Link
              to={current.secondaryLink}
              onClick={close}
              data-testid="tour-secondary-btn"
              className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-center transition flex items-center justify-center gap-1.5"
            >
              <BookOpen size={14} weight="bold" />
              {current.secondary}
            </Link>
          )}
          <button
            onClick={next}
            data-testid="tour-next-btn"
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] hover:opacity-90 rounded-xl text-sm font-semibold transition flex items-center justify-center gap-1.5 shadow-lg shadow-blue-500/30"
          >
            {current.cta}
            <ArrowRight size={14} weight="bold" />
          </button>
        </div>

        <button
          onClick={close}
          data-testid="tour-skip-btn"
          className="block mx-auto mt-4 text-xs text-white/40 hover:text-white/70 transition"
        >
          {t('tour.skip')}
        </button>
      </div>
    </div>
  );
};

export default DashboardTour;
